/**
 * Shared runtime constants (single source of truth for web + functions).
 * These are plain values (bundled by Vite on the client and tsup on the server).
 */

import type { DeliveryChannel, InboundMediaKind, UserFeatureFlags, UserPreferences } from './models'

export const COLLECTIONS = {
  users: 'users',
  secure: 'secure',
  toolCalls: 'toolCalls',
  config: 'config',
  conversations: 'conversations',
  messages: 'messages',
  folders: 'folders',
  reminders: 'reminders',
  routines: 'routines',
  clients: 'clients',
  briefings: 'briefings',
  memories: 'memories',
  emailResearch: 'emailResearch',
  usageDaily: 'usageDaily',
  adminAudit: 'adminAudit',
  /** Subcoleção secure/{uid}/accounts/{slotId} — tokens de contas conectadas ADICIONAIS. */
  connectedAccountTokens: 'accounts',
} as const

/** Allow-list configuration document: config/access */
export const CONFIG_ACCESS_DOC = 'access'

/**
 * Feature-flags document: config/features (FeaturesConfigDoc). O admin liga/desliga
 * recursos opt-in editando este doc no console do Firestore. Ausência do doc/campo
 * ⇒ recurso DESLIGADO (fail-closed). Ver isEmailResearchSendEnabled no backend.
 */
export const CONFIG_FEATURES_DOC = 'features'

/** Pricing configuration document: config/pricing (PricingConfigDoc). */
export const CONFIG_PRICING_DOC = 'pricing'

// TODO: cotacao de referencia placeholder — atualizar via config/pricing.usdToBrlRate; NAO e fonte de cambio em tempo real
export const DEFAULT_USD_BRL_RATE = 5.5

/**
 * Preços placeholder por milhão de tokens (USD), por id de modelo Gemini, mais uma
 * chave `default` para modelos sem entrada específica. NÃO são preços oficiais —
 * são estimativas para o cálculo de custo até a confirmação. Podem ser sobrescritos
 * em runtime por config/pricing.modelOverrides (PricingConfigDoc).
 */
export const GEMINI_PRICING_USD_PER_MILLION: Record<string, { input: number; output: number }> = {
  // Confirmado (preco oficial Vertex AI, texto).
  'gemini-2.5-flash': { input: 0.3, output: 2.5 },
  // REMOVIDO do catalogo/picker (CHAT_MODEL_OPTIONS) — substituido pelo
  // gemini-3.6-flash como default de chat. A tarifa PERMANECE aqui porque o
  // painel admin ainda precisa precificar uso HISTORICO (usageDaily) gravado
  // com este modelo antes da troca; nao remover sem migrar/arquivar esses dias.
  'gemini-2.5-pro': { input: 1.25, output: 10.0 },
  // Confirmado (preco oficial Vertex AI, endpoint global).
  'gemini-3.5-flash': { input: 1.5, output: 4.5 },
  // Confirmado (preco oficial Vertex AI, tier <=200k de contexto). Faixa >200k
  // nao e modelada — tabela usa tarifa unica do tier padrao. Mantido no catalogo
  // (CHAT_MODEL_OPTIONS) mas nao e mais o default de chat (ver gemini-3.8-flash).
  'gemini-3.6-flash': { input: 1.5, output: 7.5 },
  // NAO CONFIRMADO — VERIFICAR — copiado de 3.6-flash em 2026-09-05. Tentativas de
  // consultar cloud.google.com/vertex-ai/generative-ai/pricing (WebFetch) só
  // devolveram a casca da SPA (sem a tabela renderizada); buscas na web trouxeram
  // numeros DIVERGENTES entre agregadores terceiros (ex.: introdutorio 0.75/3.75
  // vs. lista 1.65/8.25 ate 2026), nenhum confirmavel na pagina oficial. Ate
  // confirmar a tarifa oficial, usa a mesma do 3.6-flash (nao subestima o custo
  // se o 3.8-flash for mais caro, mas pode SUPERESTIMAR se for mais barato).
  // Modelo default de chat.
  'gemini-3.8-flash': { input: 1.5, output: 7.5 },
  // Confirmado (preco oficial Vertex AI, tier <=200k de contexto). Faixa >200k
  // (4.00/18.00) nao e modelada — tabela usa tarifa unica do tier padrao.
  'gemini-3.1-pro-preview': { input: 2.0, output: 12.0 },
  // Voz (native audio): NAO e texto puro — cobranca POR MODALIDADE (texto-in 0.50,
  // audio-in 3.00, audio-out 12.00) e por DURACAO (~25-32 tokens/s, silencio conta).
  // Esta tabela flat nao representa isso; o custo de voz usa calculo dedicado por
  // duracao. Entradas abaixo sao so um piso defensivo (tarifas de audio in/out).
  'gemini-live-2.5-flash-native-audio': { input: 3.0, output: 12.0 },
  'gemini-live-2.5-flash-preview-native-audio-09-2025': { input: 3.0, output: 12.0 },
  // Fallback para modelo desconhecido: usa a tarifa do default de chat
  // (gemini-3.8-flash — ver ressalva VERIFICAR acima) para nao subestimar o custo.
  default: { input: 1.5, output: 7.5 },
}

/**
 * Microsoft Graph delegated scopes requested during the backend OAuth flow.
 * The same set powers the consent URL, the code exchange and the refresh-token
 * grant (all read this via getGraphScopes → requestScopes in graph/msal.ts).
 *
 * - Calendars.ReadWrite: superset of Calendars.Read (avoids a redundant scope).
 * - Chat.Create: required to open the Teams self-chat (POST /chats) in
 *   sendTeamsMessage; without it resolveChatId fails.
 * - OnlineMeetings.ReadWrite, Mail.Read: approved future capabilities.
 *
 * Mail.Send: habilita as tools sendEmail/replyEmail (Graph POST /me/sendMail e
 * /me/messages/{id}/reply) e o envio opt-in do relatório da pesquisa profunda.
 * ATENÇÃO (pendência de TI — ver pending.md): esse scope precisa de permissão
 * delegada Mail.Send + ADMIN CONSENT no Entra ID. Enquanto o consent não for
 * concedido, o fluxo OAuth pode falhar com erro de consentimento e/ou as chamadas
 * de envio retornarão 403 (tratado como "reconectar o M365"). Após o consent,
 * cada usuário precisa RECONECTAR o M365 (tokens antigos não carregam o scope).
 */
/**
 * ATENÇÃO — antes de EDITAR esta lista (adicionar/remover escopos):
 *
 * 1. Usuários que já conectaram o M365 ficam com refresh tokens DEFASADOS: o
 *    token foi emitido antes da mudança e não carrega o escopo novo. Eles
 *    precisam RECONECTAR — não basta deployar.
 * 2. Alguns escopos exigem ADMIN CONSENT no Entra ID antes de funcionar (ex.:
 *    `User.ReadBasic.All`); os demais são consentidos pelo próprio usuário ao
 *    reconectar.
 * 3. Depois de adicionar o escopo (e obter o admin consent quando aplicável),
 *    rode o script admin de reconciliação para sinalizar os usuários defasados:
 *    `node scripts/reconcile-microsoft-scopes.mjs` (dry-run) e depois `--apply`.
 *    Ele liga o flag `microsoft.reconnectRequired`, que faz a UI (banner do chat
 *    + Configurações) pedir a reconexão. Não há cron/gatilho automático — é
 *    manual, por decisão de design.
 *
 * Mecanismo: helper `missingRequiredScopes` (packages/shared/src/scopes.ts) e
 * modelo `MicrosoftIntegrationMeta.reconnectRequired` (models.ts). Pedido ao TI
 * em docs/email-ti-escopos-microsoft.txt.
 */
export const MS_GRAPH_SCOPES = [
  'offline_access',
  'openid',
  'profile',
  'email',
  'User.Read',
  'Calendars.ReadWrite',
  'ChatMessage.Send',
  'Chat.Create',
  // Required to LIST /me/chats when resolving the existing self-chat to post into
  // (Chat.Create/ChatMessage.Send do not grant reading the chat list).
  'Chat.ReadBasic',
  'OnlineMeetings.ReadWrite',
  'Mail.Read',
  // Envio/resposta de e-mail (tools sendEmail/replyEmail) + envio do relatório.
  // Requer admin consent no Entra ID + reconexão dos usuários (ver pending.md).
  'Mail.Send',
  // Pesquisar usuários do tenant (GET /users) na tool searchTenantUsers. Este
  // scope EXIGE admin consent no Entra ID (diferente dos demais) + reconexão dos
  // usuários (tokens antigos não carregam o scope).
  'User.ReadBasic.All',
] as const

/**
 * Escopos Google mínimos para a conta conectada como fonte de dados adicional:
 * agenda (eventos) + Gmail leitura + Gmail envio. `access_type=offline` +
 * `prompt=consent` (ver services/google/msal.ts) são o que garante o
 * refresh_token — não fazem parte da lista de escopos em si.
 */
export const GOOGLE_OAUTH_SCOPES = [
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/gmail.send',
] as const

/**
 * Escopos delegados para conta Microsoft EXTRA (corporativa, tenant externo,
 * segundo app registration Entra multi-tenant — ver Seção 7 do plano).
 * Deliberadamente menor que MS_GRAPH_SCOPES: sem Chat.Create, OnlineMeetings,
 * User.ReadBasic.All — recursos específicos do tenant PG (self-chat Teams,
 * diretório interno) não fazem sentido para um tenant estrangeiro.
 */
export const MS_EXTRA_ACCOUNT_SCOPES = [
  'offline_access',
  'openid',
  'profile',
  'Calendars.ReadWrite',
  'Mail.Read',
  'Mail.Send',
] as const

/** Máximo de contas Microsoft extra suportadas (fixo — 3 slots, ver ConnectedAccountSlotId). */
export const MAX_MS_EXTRA_ACCOUNTS = 3

/**
 * Taxa de conversão áudio→tokens da Live API, usada para estimar o custo de voz
 * por duração (~25 tokens/s é a cifra oficial; há relatos de campo de ~32 —
 * calibrar empiricamente contra o painel Firebase AI Logic). Aplica-se tanto ao
 * áudio de entrada quanto ao de saída; o silêncio também é cobrado.
 */
export const VOICE_AUDIO_TOKENS_PER_SECOND = 25

/** Default timezone for scheduled routines (no DST → reliable). */
export const DEFAULT_TIMEZONE = 'America/Sao_Paulo'

/**
 * URL base do app web (domínio próprio, em produção) — usada para montar links
 * apontando de volta para o app em contextos que não têm acesso ao secret
 * `APP_BASE_URL` (ex.: `emailResearch/render.ts`, puro/determinístico, montando
 * o link "Relatório completo no app" do dígest enviado ao WhatsApp).
 */
export const APP_BASE_URL = 'https://executive-agent.pgadvogados.com.br'

/** Cron expressions for scheduled routines (interpreted in DEFAULT_TIMEZONE). */
export const ROUTINE_CRON = {
  eveningBriefing: '0 20 * * *',
  morningBriefing: '0 7 * * *',
  hourlyFollowup: '0 8-20 * * *',
  memoryConsolidation: '30 3 * * *',
  emailReminders: '0 4 * * *',
} as const

/** Máximo de memórias aprovadas injetadas no resumo de contexto do agente. */
export const MEMORY_DIGEST_LIMIT = 30

/** Tamanho máximo (caracteres) do resumo de memórias injetado no prompt. */
export const MEMORY_DIGEST_MAX_CHARS = 2400

/** Limites do job diário de consolidação de memórias (controle de custo). */
export const MEMORY_JOB_LIMITS = {
  maxConversations: 10,
  maxMessagesPerConversation: 100,
  maxCharsPerMessage: 1500,
  /** Orçamento corrente de chars usado para decidir o corte por mensagem. */
  maxTotalChars: 60000,
  /**
   * Teto ABSOLUTO (soft) de caracteres do transcript inteiro, aplicado ao texto
   * já montado. Fica acima do orçamento corrente (maxTotalChars) como rede de
   * segurança: se maxTotalChars/maxCharsPerMessage subirem no futuro, o tamanho
   * do transcript continua limitado. O corte preserva as mensagens mais RECENTES
   * (as mais úteis para consolidar fatos), descartando as mais antigas.
   */
  maxTranscriptChars: 72000,
} as const

/** Limites do job diário de lembretes por e-mail (controle de custo). */
export const EMAIL_JOB_LIMITS = {
  maxEmails: 50,
  maxCharsPerEmail: 800,
  maxSuggestionsPerRun: 5,
} as const

/**
 * Limites da pesquisa profunda de e-mails (modo do secretário). Controlam o
 * custo do pipeline map/reduce: teto de e-mails buscados no Graph ($top),
 * tamanho de cada lote do passo de map, tamanho de cada lote da TRIAGEM
 * (chamadas separadas ao Gemini — enviar todos os e-mails numa única chamada
 * fazia a resposta JSON vir truncada com volume alto, ver emailResearch/retry.ts),
 * limiar acima do qual roda a triagem (reduz o volume antes do map) e o corte
 * de snippet usado na triagem.
 *
 * `batchSize` (map) e `bodyMaxChars`: incidente de produção em 2026-09-08 —
 * `summarizeBatchOnce` mandava `batchSize=100` e-mails por chamada, cada um
 * com o CORPO INTEIRO (sem corte — `fetchForResearch` em graph/live.ts não
 * limitava), somando centenas de milhares de tokens numa única chamada
 * `generateContent` (não-streaming) ao Vertex. A chamada ficou pendurada além
 * de 300s — o `headersTimeout` DEFAULT do undici (fetch global do Node) — e o
 * cliente abortou com `TypeError: fetch failed`, exatamente 300s após o log
 * "e-mails buscados" (emailCount 238). `withRetry` (retry.ts) já existia mas
 * só reaplicava a MESMA chamada gigante 3x, desperdiçando ~15min e falhando
 * de novo. Correção: `bodyMaxChars` (6000) corta o corpo de cada e-mail ANTES
 * de montar o prompt (ver `summarizeBatchOnce` em emailResearch/gemini.ts) e
 * `batchSize` caiu de 100 para 25 — com corpos de 6000 chars isso é no máximo
 * ~150 mil chars ≈ 40 mil tokens por chamada, bem abaixo do que travava. Ver
 * também `GEMINI_CALL_TIMEOUT_MS` (emailResearch/gemini.ts) para o timeout
 * explícito por chamada, que agora falha de forma rápida e distinguível em
 * vez de esperar os 300s opacos do undici.
 */
export const EMAIL_RESEARCH_LIMITS = {
  maxEmails: 1000,
  batchSize: 25,
  triageBatchSize: 150,
  triageThreshold: 500,
  snippetLen: 100,
  /** Teto de caracteres do corpo de CADA e-mail enviado ao passo de map (summarizeBatchOnce). */
  bodyMaxChars: 6000,
} as const

/**
 * Limites da busca PONTUAL de e-mails (tool searchEmails). `defaultResults` vale
 * quando o modelo não pede um limite; `maxResults` é o teto imposto no backend;
 * `scanTop` é o lote buscado no Graph quando há filtro de remetente em memória
 * (fragmento de nome, não filtrável no $filter) e o corte de prévia do corpo.
 */
export const EMAIL_SEARCH_LIMITS = {
  defaultResults: 10,
  maxResults: 25,
  scanTop: 50,
  previewLen: 200,
} as const

/**
 * True quando novas memórias exigem aprovação do usuário antes de valerem.
 * Preferência ausente ⇒ aprovação exigida (default ON). Helper puro —
 * consumido pela web e pelas functions.
 */
export function memoriesRequireApproval(prefs?: UserPreferences): boolean {
  return prefs?.memories?.requireApproval !== false
}

/**
 * Resolve os canais de entrega ativos (Teams/e-mail/WhatsApp) a partir da
 * preferência do usuário, com fallback para o campo legado `deliveryChannel`
 * e, na ausência de qualquer preferência (ou com todos os canais desligados),
 * o novo default é e-mail. Helper puro — consumido pela web e pelas functions.
 */
export function resolveChannels(prefs: UserPreferences | undefined): {
  teams: boolean
  email: boolean
  whatsapp: boolean
} {
  if (prefs?.deliveryChannels) {
    const { teams, email, whatsapp } = prefs.deliveryChannels
    const resolved = { teams: !!teams, email: !!email, whatsapp: !!whatsapp }
    if (resolved.teams || resolved.email || resolved.whatsapp) return resolved
    return { teams: false, email: true, whatsapp: false }
  }

  switch (prefs?.deliveryChannel) {
    case 'teams':
      return { teams: true, email: false, whatsapp: false }
    case 'email':
      return { teams: false, email: true, whatsapp: false }
    case 'both':
      return { teams: true, email: true, whatsapp: false }
    case 'whatsapp':
      return { teams: false, email: false, whatsapp: true }
    default:
      return { teams: false, email: true, whatsapp: false }
  }
}

/**
 * Whether a feature POR USUÁRIO (`UserFeatureFlags`) está habilitada, FAIL-CLOSED:
 * mapa ausente ou campo ausente/`false` ⇒ desligada. Helper puro — consumido pela web
 * (esconder/desabilitar UI) e pelas functions (enforcement server-side, defesa em
 * profundidade). Ver `UserDoc.features` em `models.ts`.
 */
export function isUserFeatureEnabled(
  flags: UserFeatureFlags | undefined,
  feature: keyof UserFeatureFlags,
): boolean {
  return flags?.[feature] === true
}

/** Vertex AI model routing. */
export const GEMINI_MODELS = {
  fast: 'gemini-2.5-flash',
  /** Modelo default de chat para usuários sem preferência salva (deve existir em CHAT_MODEL_OPTIONS). */
  chatDefault: 'gemini-3.8-flash',
  /** Live API model (native audio) for the real-time voice session. */
  live: 'gemini-live-2.5-flash-native-audio',
} as const

/**
 * Nome próprio da persona do agente, derivado do gênero da voz escolhida.
 * Vozes femininas falam como "Nina"; masculinas, como "Leo".
 *
 * IMPORTANTE (diagnóstico verificado em 2026-07-03): o gênero de cada voz e,
 * por consequência, as personas Nina/Leo são uma ATRIBUIÇÃO LOCAL do projeto,
 * NÃO um rótulo do Google. A doc oficial de speech do Gemini
 * (https://ai.google.dev/gemini-api/docs/speech-generation) descreve cada voz
 * apenas por timbre/estilo (ex.: Zephyr = "Bright", Puck = "Upbeat",
 * Kore = "Firm", Enceladus = "Breathy"); não há rótulo masculino/feminino oficial.
 * A validação do gênero atribuído está documentada em LIVE_VOICE_OPTIONS abaixo.
 */
export const AGENT_PERSONAS = { feminina: 'Nina', masculina: 'Leo' } as const

export type VoiceGender = 'feminina' | 'masculina'

export interface LiveVoiceOption {
  /** Prebuilt voice name aceito pelo Gemini Live (speechConfig). */
  name: string
  label: string
  gender: VoiceGender
}

export interface ModelOption {
  /** Model id aceito pelo Vertex AI via Firebase AI Logic. */
  id: string
  label: string
  /**
   * Nível de "thinking" (Gemini 3.x, `generationConfig.thinkingConfig.thinkingLevel`)
   * aplicado por padrão quando este modelo é usado no chat. Ausente ⇒ nenhum
   * thinkingConfig é injetado (deixa o SDK/modelo usar o próprio default).
   * Resolvido por `resolveChatThinkingLevel` e consumido em
   * `functions/src/agent/gemini.ts` (buildConfig).
   */
  thinkingLevel?: 'minimal' | 'low' | 'medium' | 'high'
}

/**
 * Subconjunto curado das 30 vozes native-audio do Gemini Live: expomos apenas
 * 8 destas 30 prebuilt voices; as outras 22 existem mas ficam de fora por opção
 * (a lista completa vive na doc do Google, link acima).
 *
 * O campo `gender` é uma ATRIBUIÇÃO LOCAL (ver AGENT_PERSONAS) — o Google não
 * rotula as vozes por gênero. Validação (2026-07-03): medimos a frequência
 * fundamental mediana (F0 / pitch) dos previews em apps/web/public/voices/. As 4
 * vozes `feminina` (Aoede, Kore, Leda, Zephyr) ficaram em 207–222 Hz e as 4
 * `masculina` (Puck, Charon, Orus, Fenrir) em 152–168 Hz — dois clusters limpos,
 * sem cruzamento (~40 Hz de folga), coerentes com os rótulos; também confirmado
 * de ouvido pelo usuário. As vozes "masculinas" do Gemini ficam no lado
 * agudo/andrógino (Charon "grave" mede ~168 Hz), o que é esperado.
 *
 * Ressalva: os previews são sintetizados com o modelo Gemini TTS
 * (gemini-2.5-flash-tts), enquanto a sessão de voz ao vivo usa o modelo
 * native-audio da Live API — a mesma voz pode soar um pouco diferente entre os
 * dois. Havendo divergência, julgar o gênero pela sessão ao vivo.
 */
export const LIVE_VOICE_OPTIONS = [
  { name: 'Aoede', label: 'Aoede — feminina, leve', gender: 'feminina' },
  { name: 'Kore', label: 'Kore — feminina, firme', gender: 'feminina' },
  { name: 'Leda', label: 'Leda — feminina, jovem', gender: 'feminina' },
  { name: 'Zephyr', label: 'Zephyr — feminina, clara', gender: 'feminina' },
  { name: 'Puck', label: 'Puck — masculina, animada', gender: 'masculina' },
  { name: 'Charon', label: 'Charon — masculina, grave', gender: 'masculina' },
  { name: 'Orus', label: 'Orus — masculina, firme', gender: 'masculina' },
  { name: 'Fenrir', label: 'Fenrir — masculina, intensa', gender: 'masculina' },
] as const satisfies readonly LiveVoiceOption[]

export const DEFAULT_LIVE_VOICE = 'Aoede'

/**
 * Resolve a persona do agente (nome + gênero) a partir do nome da voz escolhida.
 * Voz desconhecida ou ausente cai na voz padrão (Aoede → Nina, feminina).
 * Helper puro — consumido pela web e pelas functions.
 */
export function getPersonaForVoice(voiceName: string | undefined): {
  name: string
  gender: VoiceGender
} {
  const match = LIVE_VOICE_OPTIONS.find((v) => v.name === voiceName)
  const gender: VoiceGender =
    match?.gender ??
    LIVE_VOICE_OPTIONS.find((v) => v.name === DEFAULT_LIVE_VOICE)?.gender ??
    'feminina'
  return { name: AGENT_PERSONAS[gender], gender }
}

/** Modelos válidos para a sessão de voz (Live native-audio no Vertex). */
export const LIVE_MODEL_OPTIONS = [
  { id: GEMINI_MODELS.live, label: 'Gemini 2.5 Flash voz (estável)' },
  {
    id: 'gemini-live-2.5-flash-preview-native-audio-09-2025',
    label: 'Gemini 2.5 Flash voz (preview)',
  },
] as const satisfies readonly ModelOption[]

/** Modelos válidos para o loop do chat de texto (picker no header). */
export const CHAT_MODEL_OPTIONS = [
  // Referencia GEMINI_MODELS.chatDefault para garantir, por referência compartilhada,
  // que o default de chat sempre existe nesta allowlist. thinkingLevel 'high'
  // ativa generationConfig.thinkingConfig.thinkingLevel="HIGH" via
  // resolveChatThinkingLevel (injetado em functions/src/agent/gemini.ts).
  { id: GEMINI_MODELS.chatDefault, label: 'Gemini 3.8 Flash', thinkingLevel: 'high' },
  { id: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
  { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (preview)' },
  // gemini-2.5-pro foi REMOVIDO do catálogo (substituído primeiro pelo 3.6-flash
  // e agora pelo 3.8-flash como default) — usuários com essa preferência salva
  // caem no default via fallback de allowlist (core.ts/model-picker.tsx), sem
  // erro. A tarifa dele continua em GEMINI_PRICING_USD_PER_MILLION para
  // precificar uso histórico.
] as const satisfies readonly ModelOption[]

/**
 * Resolve o thinkingLevel configurado para um model id de chat (ver
 * CHAT_MODEL_OPTIONS), ou undefined se o modelo não tiver um nível configurado
 * ou não existir no catálogo. Helper puro — consumido por
 * functions/src/agent/gemini.ts (buildConfig) para injetar
 * generationConfig.thinkingConfig.thinkingLevel automaticamente.
 */
export function resolveChatThinkingLevel(
  modelId: string,
): ModelOption['thinkingLevel'] | undefined {
  // Cast para o tipo largo: cada literal de CHAT_MODEL_OPTIONS só carrega
  // `thinkingLevel` quando configurado, e o tipo `as const` resultante é uma
  // união desses literais — o cast é seguro porque o array já satisfaz
  // `readonly ModelOption[]` (ver `satisfies` acima).
  return (CHAT_MODEL_OPTIONS as readonly ModelOption[]).find((m) => m.id === modelId)?.thinkingLevel
}

/** Opção de canal de entrega (picker de Configurações). */
export interface DeliveryChannelOption {
  value: DeliveryChannel
  label: string
}

/**
 * Canais de entrega das mensagens proativas (briefings/lembretes), na ordem de
 * exibição no picker de Configurações. Rótulos em pt-BR. Fonte única para a UI.
 */
export const DELIVERY_CHANNEL_OPTIONS = [
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'email', label: 'E-mail' },
  { value: 'both', label: 'Teams e e-mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
] as const satisfies readonly DeliveryChannelOption[]

/**
 * Teto de SEGURANÇA (custo) da sessão de voz no cliente, em segundos (1 hora).
 *
 * NÃO é mais o limite técnico da API: a Live API derruba cada conexão WebSocket
 * por volta de ~10 min, mas a sessão agora sobrevive a isso de forma transparente
 * via session resumption + reconexão automática (ver `useVoiceSession`), então o
 * relógio da conversa é contínuo. Este teto existe apenas para não deixar uma
 * sessão esquecida aberta indefinidamente (controle de custo). Quando a
 * resumption não pôde ser habilitada (fallback), o hook cai para um teto local
 * menor (o antigo 540s) e encerra no `goingAway` como antes.
 */
export const VOICE_SESSION_HARD_CAP_SECONDS = 3600

/**
 * Compressão da janela de contexto da sessão de voz (sliding window). Mantém a
 * conversa longa dentro de um orçamento de tokens: quando o contexto (antes de um
 * turno) ultrapassa `TRIGGER`, o servidor descarta o início até chegar a `TARGET`.
 */
export const VOICE_CONTEXT_TRIGGER_TOKENS = 16000
export const VOICE_CONTEXT_TARGET_TOKENS = 8000

/** Máximo de tentativas de reconexão automática por queda de conexão da voz. */
export const VOICE_RECONNECT_MAX_ATTEMPTS = 5

/** Atraso base (ms) do backoff exponencial da reconexão da voz (BASE * 2**tentativa). */
export const VOICE_RECONNECT_BASE_DELAY_MS = 400

export const MAX_AGENT_TOOL_ITERATIONS = 6

/** Max webSearch tool calls the agent may make in a single turn. */
export const MAX_WEB_SEARCHES_PER_TURN = 3

/** Max upload size accepted for attachments (must match storage.rules). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

/**
 * Integração com o GoHighLevel (GHL) para envio de WhatsApp. Base da API,
 * versão exigida no header `Version` e os paths usados pelo GhlServices.
 */
export const GHL_BASE_URL = 'https://services.leadconnectorhq.com'

/** Versão da API do GHL exigida no header `Version` de toda requisição. */
export const GHL_API_VERSION = '2021-07-28'

/** Paths da API do GHL usados pela integração de WhatsApp. */
export const GHL_PATHS = {
  messages: '/conversations/messages',
  contactsUpsert: '/contacts/upsert',
  /**
   * Busca de contatos por texto (nome/telefone/e-mail) — GET com `?locationId=...&query=...`,
   * usada por `GhlServices.searchContacts` (tool sendWhatsappTemplateToContact).
   */
  contactsSearch: '/contacts/',
  contact: (id: string) => `/contacts/${id}`,
  /** Busca conversas de um contato — usado para resolver a janela de 24h ao vivo. */
  conversationsSearch: '/conversations/search',
} as const

/**
 * Teto de resultados devolvidos por `GhlServices.searchContacts` (tool
 * sendWhatsappTemplateToContact) — mantém o cartão de desambiguação legível e o
 * payload da resposta pequeno.
 */
export const GHL_CONTACT_SEARCH_LIMIT = 10

/**
 * Template de WhatsApp aprovado na Meta, usado por `GhlServices.sendTemplate`
 * (janela de 24h fechada). CONTRATO VALIDADO contra a API real do GHL em
 * 2026-07-22 (HTTP 201 + status delivered + variável preenchida — ver
 * `functions/src/services/ghl/live.ts#sendTemplate`). Corpo fixo aprovado com
 * uma única variável (`{{1}}`); `bodySkeleton` é esse corpo com o placeholder
 * ainda por substituir. `name` pode ser sobrescrito em runtime pela env
 * GHL_WHATSAPP_TEMPLATE_NAME (`functions/src/config.ts#getGhlWhatsappTemplateName`)
 * caso o template seja recriado com outro nome no painel do GHL/Meta — `lang`/
 * `category` não têm override porque não variam sem recriar o template.
 */
export const GHL_WHATSAPP_TEMPLATE = {
  name: 'notificacao_agente_executivo',
  lang: 'pt_BR',
  category: 'UTILITY',
  bodySkeleton:
    'Olá! Você tem uma nova notificação do Agente Executivo:\n\n{{1}}\n\nPara mais detalhes, acesse o aplicativo.',
} as const

/**
 * Teto de caracteres do conteúdo da variável `{{1}}` do template acima.
 * Templates aprovados na Meta não aceitam variáveis arbitrariamente grandes;
 * textos maiores são truncados (reticências) por `truncateForWhatsappTemplate`
 * ANTES do envio — aplicado dentro do próprio service
 * (`services/ghl/{live,stub}.ts#sendTemplate`), então vale para todo chamador
 * (rotina agendada e a tool `sendWhatsappMessage`), sem precisar duplicar o
 * corte em cada um.
 */
export const GHL_TEMPLATE_VARIABLE_MAX_CHARS = 900

/**
 * Sanitiza e trunca (reticências) o texto da variável `{{1}}` do template de
 * WhatsApp ao teto `GHL_TEMPLATE_VARIABLE_MAX_CHARS`. Helper puro — consumido
 * pelo service GHL (live/stub); nenhum chamador precisa truncar por conta
 * própria.
 *
 * A Meta rejeita parâmetros de template com quebra de linha, tab ou mais de 4
 * espaços consecutivos (erro #132018). Normaliza qualquer sequência de espaços
 * em branco para um único espaço ANTES de truncar, garantindo um parâmetro de
 * uma linha só. (Só se aplica ao template; o texto livre via sendText não tem
 * essa restrição.)
 */
export function truncateForWhatsappTemplate(text: string): string {
  const sanitized = text.replace(/\s+/g, ' ').trim()
  if (sanitized.length <= GHL_TEMPLATE_VARIABLE_MAX_CHARS) return sanitized
  return `${sanitized.slice(0, GHL_TEMPLATE_VARIABLE_MAX_CHARS - 1).trimEnd()}…`
}

// --- WhatsApp bidirecional (conversa compartilhada app <-> WhatsApp) -----------

/** Título da conversa dedicada ao canal WhatsApp (uma por usuário). */
export const WHATSAPP_CONVERSATION_TITLE = 'WhatsApp'
/** Teto de tamanho de uma mídia recebida pelo WhatsApp que enviamos ao modelo (inlineData). */
export const WHATSAPP_MEDIA_MAX_BYTES = 20 * 1024 * 1024
/** Máximo de mídias processadas por mensagem recebida (as demais são registradas como puladas). */
export const WHATSAPP_MEDIA_MAX_ITEMS = 4
/**
 * Tamanho máximo de cada mensagem de texto enviada de volta ao WhatsApp
 * (limite da Meta é 4096; margem para o `textToWhatsapp` e o marcador "(1/3)").
 */
export const WHATSAPP_REPLY_CHUNK_MAX_CHARS = 3900
/** Máximo de partes em que uma resposta longa é dividida antes de ser truncada. */
export const WHATSAPP_REPLY_MAX_CHUNKS = 3
/** Validade de uma confirmação pendente aguardando "sim"/"não" pelo WhatsApp. */
export const WHATSAPP_PENDING_CONFIRMATION_TTL_MS = 30 * 60 * 1000
/**
 * Atraso (Cloud Tasks `scheduleDelaySeconds`) aplicado ao evento SÓ-TEXTO com
 * `ghlMessageId` do webhook do GHL, para dar tempo ao possível 2º evento (o
 * MESMO `messageId` com `attachments`, ~2s depois em produção observada em
 * 2026-09-05) ser enfileirado SEM atraso e reivindicar o dedup primeiro — ver
 * `whatsapp/inboundTask.ts` (cabeçalho) para o fluxo completo.
 */
export const WHATSAPP_TEXT_ONLY_DELAY_SECONDS = 6
/** MIME types de mídia recebida que o modelo aceita como inlineData. */
export const WHATSAPP_SUPPORTED_MEDIA_MIME_PREFIXES = [
  'audio/',
  'image/',
  'video/',
  'application/pdf',
  'text/plain',
] as const

/** Classifica um MIME type recebido em `InboundMediaKind`. Helper puro. */
export function inboundMediaKindFromMime(mimeType: string): InboundMediaKind {
  const mt = mimeType.toLowerCase()
  if (mt.startsWith('audio/')) return 'audio'
  if (mt.startsWith('image/')) return 'image'
  if (mt.startsWith('video/')) return 'video'
  if (mt === 'application/pdf' || mt.startsWith('text/')) return 'document'
  return 'other'
}

/** Se o MIME type recebido pode ser enviado ao modelo como inlineData. Helper puro. */
export function isSupportedInboundMedia(mimeType: string): boolean {
  const mt = mimeType.toLowerCase()
  return WHATSAPP_SUPPORTED_MEDIA_MIME_PREFIXES.some((p) => mt.startsWith(p))
}
